"use client"

import { useState, useEffect, useMemo, useCallback } from "react"
import { useShallow } from "zustand/react/shallow"
import type { CommunityPost, FeedParams } from "../types"
import { usePostStore } from "../store/post-store"
import { useUserStore } from "../store/user-store"
import { useFeedStore } from "../store/feed-store"
import { denormalizePost } from "../normalize"
import { buildFeedKey } from "../utils"
import { loadFeed } from "../service/feed-service"

export function useFeed(params: FeedParams): {
  posts: CommunityPost[]
  loading: boolean
  hasMore: boolean
  loadMore: () => void
  refresh: () => void
} {
  const feedKey = buildFeedKey(params)
  const slice = useFeedStore((s) => s.feeds[feedKey])

  const postIds = slice?.postIds ?? []
  const postEntities = usePostStore(
    useShallow((s) => postIds.map((id) => s.entities[id])),
  )
  const authorIds = useMemo(
    () => postEntities.filter(Boolean).map((p) => p!.authorId),
    [postEntities],
  )
  const authorEntities = useUserStore(
    useShallow((s) => authorIds.map((id) => s.entities[id])),
  )

  const [loading, setLoading] = useState(!slice)

  useEffect(() => {
    setLoading(true)
    loadFeed(params).finally(() => setLoading(false))
  }, [feedKey])

  const posts = useMemo(() => {
    if (!slice) return []
    return postEntities
      .map((p) => {
        if (!p) return undefined
        const a = authorEntities.find((u) => u?.id === p.authorId)
        return a ? denormalizePost(p, a) : undefined
      })
      .filter(Boolean) as CommunityPost[]
  }, [slice, postEntities, authorEntities])

  const loadMore = useCallback(() => {
    if (!slice?.hasMore) return
    loadFeed({ ...params, page: slice.page + 1 })
  }, [params, slice])

  const refresh = useCallback(() => {
    loadFeed({ ...params, page: 1 }, true)
  }, [params])

  return { posts, loading, hasMore: slice?.hasMore ?? false, loadMore, refresh }
}
